<script setup lang="ts">
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { useAuthForm } from '@/composables/useAuthForm'
import { useRouter } from 'vue-router'
import { forgotPassword } from '@/api/auth'

const router = useRouter()

const {
  email,
  errors,
  handleSubmit,
  loading,
  showToast,
} = useAuthForm('forgotPassword')

const onSubmit = handleSubmit(async (values) => {
  loading.value = true
  try {
    await forgotPassword({
      email: values.email
    })
    showToast('success', 'Reset Link Sent', 'Please check your email for password reset instructions.')
    await router.push('/login')
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const backendErrors = error.response.data.errors
      const errorMessage = backendErrors
          .map((err: { code: string; description: string }) => err.description)
          .join('\n')
      showToast('error', 'Request Failed', errorMessage)
    } else if (error.response) {
      showToast(
          'error',
          'Request Failed',
          `Error ${error.response.status}: ${error.response.data.title || error.message}`
      )
    } else {
      showToast('error', 'Request Failed', error.message || 'An unexpected error occurred.')
    }
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="flex h-screen">
    <div class="hidden md:flex w-1/2 bg-[#f8f8f8] items-center justify-center bg-image"></div>

    <div class="w-full md:w-1/2 flex flex-col justify-center items-center px-6 sm:px-10">
      <img src="@/assets/images/logo.png" alt="Techknital Logo" class="w-48 mb-12" />

      <div class="text-center mb-12">
        <h2 class="text-3xl font-semibold text-gray-800 mb-2">Forgot Password</h2>
        <p class="text-gray-500">Enter your email to receive a password reset link.</p>
      </div>

      <form @submit.prevent="onSubmit" class="w-full max-w-sm space-y-8">
        <div>
          <label for="email" class="block text-gray-700 font-medium mb-2">Email</label>
          <InputText
              id="email"
              v-model="email"
              placeholder="Enter your Email"
              class="w-full"
              :class="{ 'p-invalid': errors.email }"
          />
          <small v-if="errors.email" class="text-red-500">{{ errors.email }}</small>
        </div>

        <Button type="submit" label="Send Reset Link" class="w-full bg-primary-500 border-none text-white text-lg py-3 rounded" :loading="loading" />

        <div class="text-center mt-4">
          <RouterLink to="/login" class="text-sm text-gray-600 underline">Back to Login</RouterLink>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.bg-image{
  background-image: url('@/assets/images/login_image.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f8f8;
}
</style>
